/**
 * O convênio do paciente na tela (#811): os textos e o corpo que vai para a
 * API. Sem React, para o teste não precisar de tela.
 *
 * ── A carteirinha na edição ──────────────────────────────────────────────
 *
 * O número inteiro nunca volta da API, então o campo nasce vazio. Vazio, na
 * edição, é "manter a que está" — a carteirinha só sai do corpo quando a
 * pessoa digita outra (troca) ou pede para tirar.
 */

import type {
  ConvenioDoPaciente,
  CriarConvenioDoPaciente,
  EditarConvenioDoPaciente,
  Operadora,
} from '@cliqcrm/contracts';

export interface FormularioDoConvenio {
  modalidade: 'particular' | 'convenio';
  operadoraId: string;
  plano: string;
  carteirinha: string;
  /** Na edição: tirar a carteirinha guardada. */
  tirarCarteirinha: boolean;
  validade: string;
  titular: boolean;
  principal: boolean;
}

export function formularioNovo(semNenhum: boolean): FormularioDoConvenio {
  return {
    modalidade: 'convenio',
    operadoraId: '',
    plano: '',
    carteirinha: '',
    tirarCarteirinha: false,
    validade: '',
    titular: true,
    // O primeiro convênio já nasce principal: o campo diz isso.
    principal: semNenhum,
  };
}

export function formularioDe(convenio: ConvenioDoPaciente): FormularioDoConvenio {
  return {
    modalidade: convenio.modalidade,
    operadoraId: convenio.operadora?.id ?? '',
    plano: convenio.plano ?? '',
    carteirinha: '',
    tirarCarteirinha: false,
    validade: convenio.validade ?? '',
    titular: convenio.titular,
    principal: convenio.principal,
  };
}

const ouNulo = (texto: string): string | null => (texto.trim() === '' ? null : texto.trim());

/** Os campos comuns: particular manda tudo nulo, como a API exige. */
function campos(formulario: FormularioDoConvenio) {
  const particular = formulario.modalidade === 'particular';
  return {
    modalidade: formulario.modalidade,
    operadora_id: particular ? null : ouNulo(formulario.operadoraId),
    plano: particular ? null : ouNulo(formulario.plano),
    validade: particular ? null : ouNulo(formulario.validade),
    titular: formulario.titular,
    principal: formulario.principal,
  };
}

export function corpoParaCriar(formulario: FormularioDoConvenio): CriarConvenioDoPaciente {
  return {
    ...campos(formulario),
    carteirinha: formulario.modalidade === 'particular' ? null : ouNulo(formulario.carteirinha),
  };
}

export function corpoParaEditar(
  formulario: FormularioDoConvenio,
  versao: number,
): EditarConvenioDoPaciente {
  const base = { ...campos(formulario), versao };
  if (formulario.modalidade === 'particular' || formulario.tirarCarteirinha) {
    return { ...base, carteirinha: null };
  }
  const nova = ouNulo(formulario.carteirinha);
  // Vazio é manter: a carteirinha não vai no corpo.
  return nova === null ? base : { ...base, carteirinha: nova };
}

/** As operadoras que a tela oferece: as ativas, mais a gravada se ela saiu. */
export function operadorasParaEscolher(
  operadoras: readonly Operadora[],
  atual: ConvenioDoPaciente['operadora'],
): { id: string; nome: string }[] {
  const ativas = operadoras.filter((o) => o.ativa).map((o) => ({ id: o.id, nome: o.nome }));
  if (atual && !ativas.some((o) => o.id === atual.id)) ativas.push(atual);
  return ativas;
}

/** `2027-03-31` → `03/2027`: a validade da carteirinha se lê por mês. */
export function mesDaValidade(validade: string): string {
  const [ano, mes] = validade.split('-');
  return `${mes}/${ano}`;
}

/** A validade já passou em `hoje` (`AAAA-MM-DD`)? */
export function vencida(validade: string | null, hoje: string): boolean {
  return validade !== null && validade < hoje;
}

/** "Saúde Vida · Ouro Enfermaria", ou "Particular". */
export function tituloDoConvenio(convenio: ConvenioDoPaciente): string {
  if (convenio.modalidade === 'particular') return 'Particular';
  return [convenio.operadora?.nome ?? 'Operadora removida', convenio.plano]
    .filter((parte): parte is string => parte !== null && parte !== '')
    .join(' · ');
}
